import React, { useState, useEffect, useRef} from 'react';
import { Container, Button, Typography, Box, LinearProgress, Autocomplete, TextField, Alert } from '@mui/material';
import axios from 'axios';
import { API_ROOT } from './config';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

function App() {
  const [file, setFile] = useState(null);
  const [context, setContext] = useState(""); // Initialize with an empty value
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("");
  const [availableContexts, setAvailableContexts] = useState([]); // This will hold contexts from backend
  const [selectedContextId, setSelectedContextId] = useState(null); // For editing purposes
  const [message, setMessage] = useState("");  // State for showing messages (success or error)
  const [severity, setSeverity] = useState("success");  // For controlling message style (success or error)
  const [timeoutDuration, setTimeoutDuration] = useState(5000);  // Default timeout duration (in milliseconds)
  const [selectedContext, setSelectedContext] = useState({});
  const [description, setDescription] = useState("");
  const [systemPrompt, setSystemPrompt] = useState("");
  const [currentLanguage, setCurrentLanguage] = useState(""); 
  const [hasStarted, setHasStarted] = useState(false);  // Track if the process has started
  const [jobs, setJobs] = useState([]);  // To store the job IDs and URLs
  const intervalRef = useRef(null); // useRef to store interval ID

  useEffect(() => {
    const fetchProtectedData = async () => {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        // If no token is found, redirect to login
        window.location.href = '/oxbo-translation-app/login';
        return;
      }
      try {
        const response = await fetch(`${API_ROOT}protected-route`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.status === 401) {
          // Token is invalid or expired, redirect to login
          localStorage.removeItem('authToken');
          window.location.href = '/oxbo-translation-app/login';
        } else {
          const data = await response.json();
          showMessage(data.message, "success", 5000);
        }
      } catch (error) {
        console.error("Error fetching protected route:", error);
      }
    };

    fetchProtectedData();
  }, []);

  // Fetch available contexts when the component mounts
  useEffect(() => {
    fetchAvailableContexts();
  }, []);

  // Effect to automatically clear the message after the specified timeout duration
  useEffect(() => {
    if (message) {
      const timeout = setTimeout(() => {
        setMessage("");  // Clear the message
      }, timeoutDuration);  // Dynamic timeout based on state

      return () => clearTimeout(timeout);  // Clear timeout if component unmounts or message changes
    }
  }, [message, timeoutDuration]);  // Run effect when 'message' or 'timeoutDuration' changes

  // Function to show messages with optional dynamic timeout and severity
  const showMessage = (msg, type = "success", duration = 5000) => {
    setMessage(msg);
    setSeverity(type);
    setTimeoutDuration(duration);  // Set dynamic timeout duration
  };

  const fetchAvailableContexts = async () => {
    try {
      const response = await axios.get(`${API_ROOT}context/contexts`); // Correct route

      // Access the 'contexts' array from the response
      if (response.data && response.data.contexts) {
        setAvailableContexts(response.data.contexts); // Set the contexts in state
        if (response.data.contexts.length > 0) {
          setSelectedContext(response.data.contexts[0].name); // Set the default selected context
        }
      } else {
        console.error('Unexpected response format:', response.data);
      }
    } catch (error) {
      console.error("Failed to fetch available contexts:", error);
    }
  };

  // Fetch context details when a context is selected
  useEffect(() => {
    const fetchContextDetails = async () => {
      try {
        const selectedContext = availableContexts.find((ctx) => ctx.name === context);
        if (selectedContext) {
          setSelectedContextId(selectedContext.id); // Set the selected context ID for editing/deleting
        }
      } catch (error) {
        console.error("Failed to fetch context details:", error);
      }
    };

    if (context && availableContexts.length > 0) {
      fetchContextDetails();
    }
  }, [context, availableContexts]);

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  const handleUpload = async () => {
    if (!file) {
      showMessage("Please select a file to upload.", "warning", 5000);
      return;
    }
    setHasStarted(true);  // Set this when the upload starts
    const formData = new FormData();
    formData.append('file', file);
    formData.append('context', systemPrompt);

    try {
      const uploadResponse = await axios.post(`${API_ROOT}upload`, formData, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = 0;
          setProgress(percentCompleted);
        }
      });

      const { job_id } = uploadResponse.data;
      setStatus("In Progress");

      // Step 2: Start polling the status endpoint for updates
      intervalRef.current = setInterval(async () => {
        try {
          // Polling status from the server
          const statusResponse = await axios.get(`${API_ROOT}status/${job_id}`);
          const result = statusResponse.data;

          // Update progress
          const progressPercent = (result.columns_translated / result.total_columns) * 100;
          setProgress(progressPercent);
          setStatus(result.status);

          // Update the current language being translated
          setCurrentLanguage(result.current_language);

          // Stop polling once the job is completed or failed
          if (result.status === "Completed" || result.status === "Failed") {
            clearInterval(intervalRef.current);  // Stop polling
            intervalRef.current = null; // Reset interval reference to null

            // Hide the progress bar and status message after completion
            setProgress(0);
            setStatus("");
            
            if (result.status === "Completed") {
              const downloadUrl = `${API_ROOT}download/${job_id}`;
              
              // Add this job to the jobs list for future reference
              setJobs(prevJobs => [...prevJobs, { jobId: job_id, url: downloadUrl }]);
            }
            
            if (result.status === "Failed") {
              showMessage("Job failed. Please try again.", "error", 5000);
            }
          }
        } catch (error) {
          console.error("Error fetching status:", error);
          clearInterval(intervalRef.current);  // Stop polling
          intervalRef.current = null; // Reset interval reference to null
          setStatus("Failed");
          showMessage("Error occurred while checking job status.", "error", 5000);
        }
      }, 1000);  // Poll every 1 second

    } catch (error) {
      console.error("Upload failed:", error);
      setStatus("Failed");
    }
  };

  const handleAddContext = async () => {
    // Make sure all fields are filled
    if (!context || !description || !systemPrompt) {
      showMessage("Please fill in all fields for the new context.", "warning", 5000);
      return; 
    }
  
    const formData = new FormData();
    formData.append('name', context);  // Using `context` directly for the name
    formData.append('description', description);  // Description must be filled
    formData.append('system_prompt', systemPrompt);  // System prompt must be filled
  
    try {
      await axios.post(`${API_ROOT}context/contexts`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      showMessage("Context added successfully", "success", 5000);
      fetchAvailableContexts();  // Refresh available contexts
  
      // Clear form after adding
      setContext("");  
      setDescription("");
      setSystemPrompt("");
    } catch (error) {
      console.error("Error adding context:", error);
      showMessage("Failed to add context", "error", 5000);
    }
  };


  const handleUpdateContext = async () => {
    if (!context || !description || !systemPrompt) {
      showMessage("All fields must be filled out", "warning", 5000);
      return;
    }
  
    const formData = new FormData();
    formData.append('name', context);  // Using `context` directly for the name
    formData.append('description', description);  // Description must be filled
    formData.append('system_prompt', systemPrompt);  // System prompt must be filled
  
    try {
      // Use data instead of FormData
      await axios.put(`${API_ROOT}context/contexts/5`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      showMessage("Context updated successfully", "success", 5000);
    } catch (error) {
      showMessage("Failed to update context", "error", 5000);
      console.error("Update error:", error);
    }
  };


  const handleDeleteContext = async () => {
    if (!selectedContextId) {
      showMessage("No context selected to delete.", "warning", 5000);
      return; 
    }

    try {
      await axios.delete(`${API_ROOT}context/contexts/${selectedContextId}`); // Correct route for deleting
      showMessage("Context deleted successfully", "success", 5000);
      
      // Refresh available contexts
      fetchAvailableContexts();
    } catch (error) {
      console.error("Failed to delete context:", error);
    }
  };

  const handleContextChange = (event, newValue) => {
    setContext(newValue);
  
    const selectedFromList = availableContexts.find((ctx) => ctx.name === newValue);
    if (selectedFromList) {
      // If the context exists, update description and prompt
      setDescription(selectedFromList.description);
      setSystemPrompt(selectedFromList.system_prompt);
      setSelectedContextId(selectedFromList.id);
    } else {
      // If it's a new context, reset description and prompt fields
      setDescription('');  // Clear the description for new context
      setSystemPrompt('');  // Clear the system prompt for new context
      setSelectedContextId(null);  // Reset the ID for new context
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4 }}>
        <Typography variant="h4" align="center" gutterBottom>
          Oxbo File Translation System
        </Typography>
        {message && (
          <Alert severity={severity} sx={{ mb: 3 }}>
            {message}
          </Alert>
        )}
        {/* File Upload Section */}
        <Box sx={{ mt: 3, mb: 2 }}>
          <input
            type="file"
            onChange={handleFileChange}
            style={{ marginBottom: '20px' }}
          />
        </Box>

        {/* Context Selector */}
        <Autocomplete
          freeSolo // Enable free typing
          value={selectedContext?.name || ""}
          onInputChange={(event, newValue) => {
            handleContextChange(event, newValue);
            // Update context name when typing or selecting from dropdown
            setSelectedContext({ name: newValue });
        
            // Check if the input matches one of the available contexts
            const selectedFromList = availableContexts.find((context) => context.name === newValue);
            
            if (selectedFromList) {
              // If a context from the list is selected, update description and prompt
              setDescription(selectedFromList.description);
              setSystemPrompt(selectedFromList.system_prompt);
            } else {
              // If a custom input, reset description and prompt
              setDescription("");
              setSystemPrompt("");
            }
          }}
          options={availableContexts.map(context => context.name)}
          renderInput={(params) => (
            <TextField 
              {...params} 
              label="Translation Context" 
              variant="outlined" 
              fullWidth
              sx={{ mb: 2 }} // Add margin-bottom to create space below this component
            />
          )}
        />

        {/* Editable Description */}
        <TextField
          label="Description"
          variant="outlined"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          sx={{ mb: 2 }} // Add margin-bottom for spacing
        />

        <TextField
          label="System Prompt"
          variant="outlined"
          value={systemPrompt}
          onChange={(e) => setSystemPrompt(e.target.value)}
          fullWidth
          multiline
          sx={{ mb: 3 }} // Add margin-bottom for more space
        />


        {/* Edit and Delete Context Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>

        <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteContext}
          >
            Delete Context
          </Button>

          <Button
            variant="contained"
            color="secondary"
            startIcon={<EditIcon />}
            onClick={handleUpdateContext}
          >
            Update Context
          </Button>

          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddContext}
          >
            Add Context
          </Button>

        </Box>

        {/* Upload Button */}
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleUpload}
          sx={{ mb: 4 }}
        >
          Upload and Translate
        </Button>

        {/* Conditionally render status and progress only if there's progress or status */}
        {(hasStarted && (progress > 0 || status !== "")) && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6">Translation Status</Typography>
            <Typography>{status}</Typography>
            {currentLanguage && <Typography>Current Language: {currentLanguage}</Typography>}
            <LinearProgress variant="determinate" value={progress} sx={{ height: 10, borderRadius: 5 }} />
            <Typography sx={{ mt: 2 }}>{Math.round(progress)}%</Typography>
          </Box>
        )}
        {/* Display a list of job download links */}
        {jobs.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6">Download Completed Jobs</Typography>
            <ul>
              {jobs.map((job, index) => (
                <li key={job.jobId}>
                  <a href={job.url} download>{`Job ${index + 1} - Download`}</a>
                </li>
              ))}
            </ul>
          </Box>
        )}
      </Box>
    </Container>
  );
}

export default App;